import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  clearTokens,
  getAccessToken,
  getRefreshToken,
  storeTokens,
  subscribeToTokenChanges,
} from './tokenStorage';

export type UserRole = 'Viewer' | 'Editor' | 'Admin';

// Decode JWT payload without validation (validation is done server-side).
function parseTokenPayload(accessToken: string): { role: UserRole | null; userId: string | null } {
  try {
    const segment = accessToken.split('.')[1];
    if (!segment) return { role: null, userId: null };
    // base64url → base64 with padding
    const padded = segment
      .replace(/-/g, '+')
      .replace(/_/g, '/')
      .padEnd(segment.length + ((4 - (segment.length % 4)) % 4), '=');
    const payload = JSON.parse(atob(padded)) as Record<string, unknown>;
    const r = payload['role'];
    const role: UserRole | null =
      r === 'Viewer' || r === 'Editor' || r === 'Admin' ? r : null;
    const userId =
      (payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'] as string | undefined) ??
      (payload['sub'] as string | undefined) ??
      null;
    return { role, userId };
  } catch {
    return { role: null, userId: null };
  }
}

interface AuthState {
  accessToken: string | null;
  role: UserRole | null;
  userId: string | null;
  setTokens: (accessToken: string, refreshToken: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [accessToken, setAccessToken] = useState<string | null>(getAccessToken);
  const [role, setRole] = useState<UserRole | null>(() => {
    const token = getAccessToken();
    return token ? parseTokenPayload(token).role : null;
  });
  const [userId, setUserId] = useState<string | null>(() => {
    const token = getAccessToken();
    return token ? parseTokenPayload(token).userId : null;
  });

  const refreshTokenRef = useRef<string | null>(getRefreshToken());

  const applyToken = useCallback((token: string | null) => {
    const parsed = token ? parseTokenPayload(token) : { role: null, userId: null };
    setAccessToken(token);
    setRole(parsed.role);
    setUserId(parsed.userId);
  }, []);

  // Another tab logged in, refreshed or logged out — follow it.
  useEffect(
    () =>
      subscribeToTokenChanges(() => {
        applyToken(getAccessToken());
        refreshTokenRef.current = getRefreshToken();
      }),
    [applyToken]
  );

  const setTokens = useCallback(
    (at: string, rt: string) => {
      applyToken(at);
      refreshTokenRef.current = rt;
      storeTokens(at, rt);
    },
    [applyToken]
  );

  const logout = useCallback(() => {
    applyToken(null);
    refreshTokenRef.current = null;
    clearTokens();
  }, [applyToken]);

  return (
    <AuthContext.Provider value={{ accessToken, role, userId, setTokens, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
